/* eslint-disable deprecation/deprecation */
import {
  capitalize,
  getStepIconUtilityClass,
  useThemeProps,
  type StepIconProps,
} from '@mui/material';
import composeClasses from '@mui/utils/composeClasses';
import { getToken } from '@workiva/unify-tokens';
import clsx from 'clsx';
import * as React from 'react';
import { MaterialSymbolsIconProps } from 'src/components/MaterialSymbolsIcon/MaterialSymbolsIcon';
import UnifyIcons from 'src/components/UnifyIcons';
import { type StepSeverity } from './StepSeverity';
import type { StepLabelProps } from './StepLabel';

type StepIconOwnerState = Required<
  Pick<StepIconProps, 'completed' | 'active'>
> &
  Pick<StepIconProps, 'classes'>;

type UnifySeverityStepIconOwnerState = {
  severity: Required<StepLabelProps['severity']>;
};

export type UnifySeverityStepIconProps = StepIconProps & {
  ownerState: UnifySeverityStepIconOwnerState;
};

export const UnifySeverityStepIcon: React.FC<UnifySeverityStepIconProps> = (
  inProps
) => {
  const props = useThemeProps({ props: inProps, name: 'MuiStepIcon' });
  const {
    classes: classesProp,
    className: classNameProp,
    ownerState,
    // Intentionally not forwarded to avoid setting an invalid prop on the DOM element.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error,
    completed = false,
    active = false,
    sx: sxIn,
    ...rest
  } = props;
  const { severity } = ownerState;
  const classes = useUtilityClasses({
    severity,
    completed,
    active,
    classes: classesProp,
  });
  const className = clsx(classNameProp, classes.root);
  const iconComponentsAndColors: {
    [key in Exclude<StepSeverity, 'default'>]: {
      Component: React.FC<MaterialSymbolsIconProps>;
      color: string;
    };
  } = {
    error: {
      Component: UnifyIcons.DoNotDisturbOnFilled,
      color: getToken('Core/Icon/icon-error'),
    },
    warning: {
      Component: UnifyIcons.WarningFilled,
      color: getToken('Core/Icon/icon-warning'),
    },
    success: {
      Component: UnifyIcons.CheckCircleFilled,
      color: getToken('Core/Icon/icon-success'),
    },
    inProgress: {
      Component: UnifyIcons.ChangeCircleFilled,
      color: getToken('Core/Icon/icon-inProgress'),
    },
  };

  const { Component: IconComponent, color: colorVar } =
    severity && severity !== 'default'
      ? iconComponentsAndColors[severity]
      : {
          Component: UnifyIcons.CheckCircleFilled,
          color: getToken('Core/Icon/icon-primary'),
        };

  return (
    <IconComponent
      {...rest}
      ref={null} // Can't pass a div ref to a span component
      className={className}
      sx={{ ...sxIn, color: colorVar }}
    />
  );
};

const useUtilityClasses = (
  ownerState: UnifySeverityStepIconOwnerState & StepIconOwnerState
) => {
  const { classes, active, completed, severity } = ownerState;

  const slots = {
    root: [
      'root',
      active && 'active',
      completed && 'completed',
      severity && `severity${capitalize(severity)}`,
    ],
  };

  return composeClasses(slots, getStepIconUtilityClass, classes);
};

export default UnifySeverityStepIcon;
